import { audio } from '../../../../../audio/AudioManager.js';
import { SaveManager } from '../../../../../state/SaveManager.js';
import { applicationLifecycle } from '../../../../shared/lifecycle/index.js';
import { ensureMinimumTextTouchTarget } from '../../../../ui/TouchTarget.js';
import { NUMBER_KINGDOM_ASSETS } from '../../NumberKingdomAssets.js';
import {
  NUMBER_KINGDOM_DISTRICTS,
  completeNumberKingdomDistrictProgress,
  getNumberKingdomState,
  saveNumberKingdomDistrictProgress,
  visitNumberKingdomDistrict,
} from '../../NumberKingdomState.js';
import {
  configureNumberKingdomDistrictRenderer,
  getNumberKingdomDistrictLayout,
} from '../../components/NumberKingdomDistrictLayout.js';
import { createNumberKingdomHomeButton, type NumberKingdomHomeButton } from '../../components/NumberKingdomHomeButton.js';
import { SUBTRACTION_KEEP_ASSETS } from '../subtraction-keep/SubtractionKeepAssets.js';
import {
  MEASUREMENT_MARKET_ASSETS,
  MEASUREMENT_MARKET_ASSET_LIST,
  canEnterMeasurementMarket,
  getMissingMeasurementMarketAssets,
} from './MeasurementMarketAssets.js';
import {
  MEASUREMENT_MARKET_CHALLENGES,
  type MeasurementMarketChallengeConfig,
  type MeasurementMarketItem,
} from './MeasurementMarketConfig.js';
import {
  MeasurementMarketState,
  type MeasurementMarketSerializedState,
} from './MeasurementMarketState.js';
import { canEnterClocktowerPlaza } from '../clocktower-plaza/ClocktowerPlazaAssets.js';

export const MEASUREMENT_MARKET_SCENE_KEY = 'MeasurementMarket';
const FONT_UI = '"Nunito", "Segoe UI", Arial, sans-serif';

interface MeasurementMarketProgress {
  readonly version: 1;
  readonly challengeIndex: number;
  readonly challengeId: string;
  readonly challengeState: MeasurementMarketSerializedState;
  readonly completedChallengeIds: readonly string[];
  readonly restored: boolean;
}

interface ArtButton {
  readonly image: Phaser.GameObjects.Image;
  readonly label: Phaser.GameObjects.Text;
  readonly action: () => void;
}

export class MeasurementMarketScene extends Phaser.Scene {
  static readonly requiredImageKeys = MEASUREMENT_MARKET_ASSET_LIST.map(asset => asset.key);

  private challengeIndex = 0;
  private completedChallengeIds = new Set<string>();
  private challenge!: MeasurementMarketState;
  private dormantBackground!: Phaser.GameObjects.Image;
  private restoredBackground!: Phaser.GameObjects.Image;
  private instrument!: Phaser.GameObjects.Image;
  private missionPanel!: Phaser.GameObjects.Image;
  private cosmo!: Phaser.GameObjects.Image;
  private titleText!: Phaser.GameObjects.Text;
  private missionText!: Phaser.GameObjects.Text;
  private phaseText!: Phaser.GameObjects.Text;
  private feedbackText!: Phaser.GameObjects.Text;
  private controlsText!: Phaser.GameObjects.Text;
  private backText!: Phaser.GameObjects.Text;
  private homeButton!: NumberKingdomHomeButton;
  private nextText!: Phaser.GameObjects.Text;
  private actionButtons: ArtButton[] = [];
  private choiceButtons: ArtButton[] = [];
  private tickLabels: Phaser.GameObjects.Text[] = [];
  private tickZones: Phaser.GameObjects.Zone[] = [];
  private goods: Phaser.GameObjects.Image[] = [];
  private recordTexts: Phaser.GameObjects.Text[] = [];
  private restored = false;
  private suspended = false;
  private leaving = false;
  private reducedMotion = false;
  private resizeTimer?: number;
  private unsubscribeLifecycle?: () => void;

  constructor() { super({ key: MEASUREMENT_MARKET_SCENE_KEY }); }

  create() {
    this.actionButtons = [];
    this.choiceButtons = [];
    this.tickLabels = [];
    this.tickZones = [];
    this.goods = [];
    this.recordTexts = [];
    this.challengeIndex = 0;
    this.completedChallengeIds = new Set<string>();
    this.leaving = false;
    this.suspended = false;

    const world = getNumberKingdomState();
    const missing = getMissingMeasurementMarketAssets(this.textures);
    if (!world.districtUnlocks.includes(NUMBER_KINGDOM_DISTRICTS.measureworks)
      || !canEnterMeasurementMarket(this.textures)) {
      if (missing.length > 0) {
        console.error('[MeasurementMarket] Required production textures are missing:',
          missing.map(asset => `${asset.key} (${asset.path})`));
      }
      this.scene.start('SubtractionKeep');
      return;
    }

    configureNumberKingdomDistrictRenderer(this);
    void audio.enterSoundscape('number-kingdom');
    this.reducedMotion = SaveManager.getSettings().reducedMotion
      || (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
    this.leaving = false;
    this.suspended = false;
    this.restored = world.districtCompletion[NUMBER_KINGDOM_DISTRICTS.measureworks] === true;
    this.restoreProgress(world.districtProgress[NUMBER_KINGDOM_DISTRICTS.measureworks]);
    visitNumberKingdomDistrict(NUMBER_KINGDOM_DISTRICTS.measureworks);
    SaveManager.setLastScene(MEASUREMENT_MARKET_SCENE_KEY);
    SaveManager.save();

    this.buildEnvironment();
    this.buildInterface();
    this.registerInput();
    this.registerLifecycle();
    this.refresh();
    this.cameras.main.fadeIn(this.reducedMotion ? 140 : 420, 255, 225, 170);
  }

  private restoreProgress(value: unknown) {
    const progress = value as Partial<MeasurementMarketProgress> | undefined;
    if (progress?.version === 1) {
      this.challengeIndex = Phaser.Math.Clamp(
        Number.isInteger(progress.challengeIndex) ? Number(progress.challengeIndex) : 0,
        0,
        MEASUREMENT_MARKET_CHALLENGES.length - 1,
      );
      this.completedChallengeIds = new Set(
        (progress.completedChallengeIds ?? []).filter(id =>
          MEASUREMENT_MARKET_CHALLENGES.some(config => config.id === id)),
      );
      this.restored ||= progress.restored === true;
      const config = MEASUREMENT_MARKET_CHALLENGES[this.challengeIndex];
      const saved = progress.challengeId === config.id ? progress.challengeState : undefined;
      this.challenge = new MeasurementMarketState(config, saved);
      return;
    }
    this.challenge = new MeasurementMarketState(MEASUREMENT_MARKET_CHALLENGES[0]);
  }

  private buildEnvironment() {
    this.dormantBackground = this.add.image(640, 360, MEASUREMENT_MARKET_ASSETS.dormant.key)
      .setDisplaySize(1280, 720).setDepth(0);
    this.restoredBackground = this.add.image(640, 360, MEASUREMENT_MARKET_ASSETS.restored.key)
      .setDisplaySize(1280, 720).setDepth(1).setAlpha(this.restored ? 1 : 0);
    this.instrument = this.add.image(640, 410, MEASUREMENT_MARKET_ASSETS.instrument.key).setDepth(35);
    this.missionPanel = this.add.image(640, 96, SUBTRACTION_KEEP_ASSETS.missionPanel.key).setDepth(40);
    this.cosmo = this.add.image(110, 640, NUMBER_KINGDOM_ASSETS.numberKingdomCosmo.key).setDepth(100);
  }

  private buildInterface() {
    this.titleText = this.makeText('MEASUREMENT MARKET', 28, '#fff2ad', 'bold')
      .setStroke('#2c1746', 7).setDepth(110);
    this.missionText = this.makeText('', 16, '#ffffff', 'bold')
      .setStroke('#233a69', 5).setWordWrapWidth(680).setDepth(110);
    this.phaseText = this.makeText('', 24, '#fff2ad', 'bold')
      .setStroke('#26375c', 7).setWordWrapWidth(800).setDepth(110);
    this.feedbackText = this.makeText('', 15, '#ffffff', 'bold')
      .setStroke('#26375c', 5).setWordWrapWidth(760).setDepth(110);
    this.controlsText = this.makeText(
      'Tap a choice or ruler tick  •  arrows change selection  •  Enter check/next  •  H hint  •  R reset',
      11,
      '#fff8db',
      'bold',
    ).setStroke('#26375c', 5).setDepth(110);
    this.backText = this.makeText('←  SUBTRACTION KEEP', 14, '#ffffff', 'bold')
      .setStroke('#26375c', 6).setInteractive({ useHandCursor: true }).setDepth(112);
    this.homeButton = createNumberKingdomHomeButton(this, this.returnHome);
    this.nextText = this.makeText('CLOCKTOWER PLAZA  →  (K)', 14, '#fff2ad', 'bold')
      .setStroke('#26375c', 6).setInteractive({ useHandCursor: true }).setDepth(112);
    [this.backText, this.nextText].forEach(target => ensureMinimumTextTouchTarget(target));
    this.backText.on('pointerup', this.returnToKeep, this);
    this.nextText.on('pointerup', this.travelToClocktower, this);

    this.actionButtons = [
      this.makeButton('CHECK', () => this.primaryAction()),
      this.makeButton('HINT', () => this.hint()),
      this.makeButton('RESET', () => this.resetChallenge()),
    ];
    for (let value = 0; value <= 12; value += 1) {
      const label = this.makeText(String(value), 16, '#ffffff', 'bold')
        .setStroke('#26375c', 5).setDepth(85);
      const zone = this.add.zone(0, 0, 44, 52).setDepth(86).setInteractive({ useHandCursor: true });
      zone.on('pointerup', () => this.select(value));
      this.tickLabels.push(label);
      this.tickZones.push(zone);
    }
  }

  private makeText(value: string, size: number, color: string, style: string) {
    return this.add.text(0, 0, value, {
      fontFamily: FONT_UI,
      fontSize: `${size}px`,
      fontStyle: style,
      color,
      align: 'center',
    }).setOrigin(0.5);
  }

  private makeButton(label: string, action: () => void): ArtButton {
    const image = this.add.image(0, 0, SUBTRACTION_KEEP_ASSETS.actionButton.key)
      .setDepth(100).setInteractive({ useHandCursor: true });
    const text = this.makeText(label, 13, '#ffffff', 'bold').setStroke('#26375c', 4).setDepth(101);
    image.on('pointerup', action);
    return { image, label: text, action };
  }

  private select(value: number) {
    if (!this.canAct() || !this.challenge.select(value)) return;
    this.feedbackText.setText(`Selected ${value}. Check the measurement when you are ready.`);
    audio.click();
    this.capture();
  }

  private primaryAction() {
    if (this.restored) return;
    if (this.challenge.snapshot.phase === 'complete') return this.startNextChallenge();
    const result = this.challenge.check();
    if (result === 'not-ready') {
      this.feedbackText.setText('Choose an estimate or measurement first. You can revise it before checking.');
      audio.wrong();
    } else if (result === 'incorrect') {
      this.feedbackText.setText('That does not fit the market record yet. Look again and revise your choice.');
      audio.wrong();
    } else if (result === 'estimate-complete') {
      this.feedbackText.setText('Good estimate! The tick labels light up—now measure the exact length.');
      this.playMagicBurst(640, 390, 250);
      audio.correct();
    } else {
      const config = this.currentConfig;
      this.completedChallengeIds.add(config.id);
      SaveManager.recordAnswer(config.skillId, true);
      this.feedbackText.setText('Order measured correctly. The market mechanisms power on!');
      audio.correct();
      if (this.completedChallengeIds.size === MEASUREMENT_MARKET_CHALLENGES.length) {
        this.completeDistrict();
      } else {
        this.actionButtons[0].label.setText('NEXT ORDER');
        this.playMagicBurst(640, 390, 320);
      }
    }
    this.capture();
  }

  private startNextChallenge() {
    if (this.restored || this.challenge.snapshot.phase !== 'complete') return;
    const nextIndex = MEASUREMENT_MARKET_CHALLENGES.findIndex(
      config => !this.completedChallengeIds.has(config.id),
    );
    if (nextIndex < 0) return this.completeDistrict();
    this.challengeIndex = nextIndex;
    this.challenge = new MeasurementMarketState(this.currentConfig);
    this.feedbackText.setText('A new market order rolls onto the measuring table.');
    this.actionButtons[0].label.setText('CHECK');
    this.capture();
  }

  private completeDistrict() {
    this.restored = true;
    this.completedChallengeIds = new Set(MEASUREMENT_MARKET_CHALLENGES.map(config => config.id));
    completeNumberKingdomDistrictProgress(
      NUMBER_KINGDOM_DISTRICTS.measureworks,
      this.progressSnapshot(),
      NUMBER_KINGDOM_DISTRICTS.clockTower,
    );
    this.restoredBackground.setAlpha(1);
    this.dormantBackground.setAlpha(0);
    this.feedbackText.setText('Every order is measured. The market glows and the Clocktower route opens!');
    this.playMagicBurst(640, 355, 560);
    audio.restoration();
  }

  private hint() {
    if (!this.canAct()) return;
    this.feedbackText.setText(this.challenge.requestHint() ?? 'Compare the item endpoints carefully.');
    audio.click();
    this.capture();
  }

  private resetChallenge() {
    if (!this.canAct()) return;
    this.challenge = new MeasurementMarketState(this.currentConfig);
    this.feedbackText.setText('This order is reset. Completed market orders are still safe.');
    audio.click();
    this.capture();
  }

  private capture() {
    saveNumberKingdomDistrictProgress(NUMBER_KINGDOM_DISTRICTS.measureworks, this.progressSnapshot());
    this.refresh();
  }

  private progressSnapshot(): MeasurementMarketProgress {
    return {
      version: 1,
      challengeIndex: this.challengeIndex,
      challengeId: this.currentConfig.id,
      challengeState: this.challenge.serialize(),
      completedChallengeIds: [...this.completedChallengeIds],
      restored: this.restored,
    };
  }

  private refresh() {
    const snapshot = this.challenge.snapshot;
    const config = this.currentConfig;
    this.restoredBackground.setAlpha(this.restored ? 1 : 0);
    this.dormantBackground.setAlpha(this.restored ? 0 : 1);
    this.instrument.setVisible(!this.restored);
    this.missionText.setText(this.restored
      ? 'The market is restored. The open arch now leads toward Clocktower Plaza.'
      : config.kind === 'measure'
        ? `Order ${this.challengeIndex + 1} of ${MEASUREMENT_MARKET_CHALLENGES.length}: ${config.itemLabel}, measured in ${config.unit}.`
        : `Order ${this.challengeIndex + 1} of ${MEASUREMENT_MARKET_CHALLENGES.length}: use the saved market measurements.`);
    this.phaseText.setText(this.restored
      ? 'MARKET RESTORED'
      : config.kind === 'data'
        ? config.prompt
        : snapshot.phase === 'estimate'
          ? `About how many ${config.unit} long is the ${config.itemLabel}?`
          : snapshot.phase === 'measure'
            ? config.start === 0
              ? `Which tick shows the exact length in ${config.unit}?`
              : `It starts at ${config.start} and ends at ${config.end}. What is its length?`
            : 'ORDER MEASURED');
    this.actionButtons.forEach(button => {
      button.image.setVisible(!this.restored);
      button.label.setVisible(!this.restored);
    });
    this.actionButtons[0].label.setText(snapshot.phase === 'complete' ? 'NEXT ORDER' : 'CHECK');
    this.controlsText.setVisible(!this.restored);
    this.nextText.setVisible(this.restored);
    if (!this.feedbackText.text) {
      this.feedbackText.setText('Estimate first, then use the instrument to make an exact measurement.');
    }
    this.renderGoods();
    this.renderChoiceButtons();
    this.layout();
  }

  private renderGoods() {
    this.goods.forEach(item => item.destroy());
    this.recordTexts.forEach(item => item.destroy());
    this.goods = [];
    this.recordTexts = [];
    if (this.restored) return;
    const config = this.currentConfig;
    if (config.kind === 'measure') {
      this.goods.push(this.add.image(0, 0, this.itemKey(config.item)).setDepth(72));
      return;
    }
    this.goods.push(
      this.add.image(0, 0, MEASUREMENT_MARKET_ASSETS.ribbon.key).setDepth(72),
      this.add.image(0, 0, MEASUREMENT_MARKET_ASSETS.parcel.key).setDepth(72),
    );
    this.recordTexts.push(
      this.makeText('RIBBON  6 inches', 14, '#ffffff', 'bold').setStroke('#26375c', 5).setDepth(90),
      this.makeText('PARCEL  8 inches', 14, '#ffffff', 'bold').setStroke('#26375c', 5).setDepth(90),
    );
  }

  private renderChoiceButtons() {
    this.choiceButtons.forEach(button => {
      button.image.destroy();
      button.label.destroy();
    });
    this.choiceButtons = this.challenge.snapshot.choices.map(value =>
      this.makeButton(String(value), () => this.select(value)));
  }

  private layout() {
    const { safe, compact } = getNumberKingdomDistrictLayout(this);
    const portrait = safe.width < 700;
    const panelWidth = portrait
      ? Math.min(500, safe.width - 30)
      : Math.min(compact ? 650 : 740, safe.width - 230);
    this.missionPanel.setPosition(safe.centerX, safe.y + (portrait ? 126 : compact ? 82 : 92))
      .setDisplaySize(panelWidth, panelWidth * (360 / 900));
    this.titleText.setPosition(safe.centerX, safe.y + (portrait ? 58 : 25))
      .setFontSize(portrait ? 22 : compact ? 22 : 28);
    this.missionText.setPosition(safe.centerX, safe.y + (portrait ? 132 : compact ? 87 : 98))
      .setFontSize(portrait ? 12 : compact ? 13 : 16).setWordWrapWidth(panelWidth * 0.8);
    this.backText.setPosition(safe.x + (portrait ? 238 : compact ? 250 : 282), safe.y + 30)
      .setFontSize(portrait ? 9 : compact ? 11 : 14);
    this.homeButton.layout(safe, compact || portrait, 'left');
    this.nextText.setPosition(safe.centerX, safe.bottom - 42).setFontSize(portrait ? 11 : 14);
    this.phaseText.setPosition(safe.centerX, safe.y + (portrait ? 228 : compact ? 178 : 195))
      .setFontSize(portrait ? 20 : compact ? 21 : 24)
      .setWordWrapWidth(portrait ? safe.width - 45 : Math.min(800, safe.width - 220));

    const instrumentWidth = portrait ? safe.width - 34 : compact ? 820 : 900;
    const instrumentHeight = instrumentWidth * (360 / 1024);
    const instrumentY = safe.y + (portrait ? 384 : compact ? 365 : 390);
    this.instrument.setPosition(safe.centerX, instrumentY).setDisplaySize(instrumentWidth, instrumentHeight);
    const tickSpan = instrumentWidth * 0.657;
    const tickStart = safe.centerX - tickSpan / 2;
    const tickGap = tickSpan / 12;
    const tickY = instrumentY + instrumentHeight * 0.045;
    const exactMeasure = this.currentConfig.kind === 'measure'
      && this.challenge.snapshot.phase === 'measure';
    this.tickLabels.forEach((label, value) => {
      const selected = this.challenge.snapshot.selectedValue === value;
      label.setPosition(tickStart + value * tickGap, tickY)
        .setFontSize(portrait ? 10 : compact ? 13 : 16)
        .setColor(selected ? '#fff2ad' : '#ffffff')
        .setVisible(exactMeasure || this.currentConfig.kind === 'data');
      const zone = this.tickZones[value];
      zone.setPosition(label.x, tickY).setSize(44, 52);
      if (exactMeasure && !portrait && !this.restored) zone.setInteractive({ useHandCursor: true });
      else zone.disableInteractive();
    });

    if (this.currentConfig.kind === 'measure' && this.goods[0]) {
      const startX = tickStart + this.currentConfig.start * tickGap;
      const endX = tickStart + this.currentConfig.end * tickGap;
      this.goods[0].setPosition((startX + endX) / 2, instrumentY - instrumentHeight * 0.22)
        .setDisplaySize(Math.max(70, endX - startX), portrait ? 48 : compact ? 58 : 68);
    } else if (this.goods.length === 2) {
      const goodsY = instrumentY - instrumentHeight * 0.26;
      const offset = portrait ? 105 : 190;
      this.goods[0].setPosition(safe.centerX - offset, goodsY).setDisplaySize(portrait ? 140 : 220, portrait ? 70 : 100);
      this.goods[1].setPosition(safe.centerX + offset, goodsY).setDisplaySize(portrait ? 140 : 220, portrait ? 70 : 100);
      this.recordTexts[0].setPosition(safe.centerX - offset, goodsY - (portrait ? 50 : 68)).setFontSize(portrait ? 10 : 14);
      this.recordTexts[1].setPosition(safe.centerX + offset, goodsY - (portrait ? 50 : 68)).setFontSize(portrait ? 10 : 14);
    }

    const showChoicePalette = !exactMeasure || portrait;
    const choiceCount = this.choiceButtons.length;
    this.choiceButtons.forEach((button, index) => {
      button.image.setVisible(showChoicePalette && !this.restored);
      button.label.setVisible(showChoicePalette && !this.restored);
      if (!showChoicePalette) return;
      if (choiceCount <= 3) {
        const gap = portrait ? 132 : 150;
        const x = safe.centerX + (index - 1) * gap;
        const y = safe.bottom - (compact ? 148 : 165);
        button.image.setPosition(x, y).setDisplaySize(portrait ? 112 : 128, portrait ? 56 : 64);
        button.label.setPosition(x, y).setFontSize(portrait ? 12 : 14);
      } else {
        const column = index % 7;
        const row = Math.floor(index / 7);
        const width = Math.min(58, (safe.width - 45) / 7);
        const x = safe.centerX + (column - 3) * width;
        const y = safe.bottom - 198 + row * 48;
        button.image.setPosition(x, y).setDisplaySize(width - 4, 42);
        button.label.setPosition(x, y).setFontSize(11);
      }
    });

    const buttonY = safe.bottom - (compact ? 68 : 76);
    const gap = portrait ? Math.min(150, (safe.width - 120) / 2) : compact ? 145 : 165;
    this.actionButtons.forEach((button, index) => {
      const x = safe.centerX + (index - 1) * gap;
      button.image.setPosition(x, buttonY)
        .setDisplaySize(portrait ? 112 : compact ? 128 : 145, portrait ? 56 : compact ? 64 : 72);
      button.label.setPosition(x, buttonY).setFontSize(portrait ? 10 : compact ? 12 : 14);
    });
    this.feedbackText.setPosition(safe.centerX, safe.bottom - (compact ? 113 : 128))
      .setFontSize(portrait ? 11 : compact ? 12 : 15)
      .setWordWrapWidth(portrait ? safe.width - 45 : Math.min(760, safe.width - 230));
    this.controlsText.setPosition(safe.centerX, safe.bottom - 18)
      .setFontSize(portrait ? 8 : compact ? 9 : 11)
      .setWordWrapWidth(portrait ? safe.width - 30 : safe.width);
    this.cosmo.setPosition(safe.x + (compact ? 56 : 72), safe.bottom - (compact ? 48 : 58))
      .setDisplaySize(compact ? 84 : 104, compact ? 76 : 93);
  }

  private itemKey(item: MeasurementMarketItem) {
    if (item === 'ribbon') return MEASUREMENT_MARKET_ASSETS.ribbon.key;
    if (item === 'rod') return MEASUREMENT_MARKET_ASSETS.rod.key;
    return MEASUREMENT_MARKET_ASSETS.parcel.key;
  }

  private playMagicBurst(x: number, y: number, size: number) {
    const burst = this.add.image(x, y, SUBTRACTION_KEEP_ASSETS.magicBurst.key)
      .setDisplaySize(size, size).setDepth(95).setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(this.reducedMotion ? 0.68 : 0);
    if (this.reducedMotion) this.time.delayedCall(650, () => burst.destroy());
    else this.tweens.add({
      targets: burst,
      alpha: { from: 0, to: 0.9 },
      scaleX: 1.12,
      scaleY: 1.12,
      duration: 500,
      yoyo: true,
      hold: 120,
      onComplete: () => burst.destroy(),
    });
  }

  private registerInput() {
    this.input.keyboard?.on('keydown', this.handleKeyDown, this);
  }

  private handleKeyDown(event: KeyboardEvent) {
    const key = event.key.toLocaleLowerCase();
    if (key === 'escape') return this.returnToKeep();
    if (key === 'm') return this.returnHome();
    if (key === 'k' && this.restored) return this.travelToClocktower();
    if (key === 'enter' || key === ' ') return this.primaryAction();
    if (key === 'h') return this.hint();
    if (key === 'r') return this.resetChallenge();
    if (/^[0-9]$/.test(key)) return this.select(Number(key));
    if (key === 'arrowleft' || key === 'a') return this.moveSelection(-1);
    if (key === 'arrowright' || key === 'd') this.moveSelection(1);
  }

  private moveSelection(delta: number) {
    if (!this.canAct()) return;
    const choices = this.challenge.snapshot.choices;
    const current = this.challenge.snapshot.selectedValue;
    const currentIndex = current === undefined ? (delta > 0 ? -1 : 0) : choices.indexOf(current);
    const nextIndex = Phaser.Math.Wrap(currentIndex + delta, 0, choices.length);
    this.select(choices[nextIndex]);
  }

  private registerLifecycle() {
    this.unsubscribeLifecycle = applicationLifecycle.subscribe({
      onSuspend: () => { this.suspended = true; },
      onResume: () => { this.suspended = false; },
    });
    window.addEventListener('resize', this.handleResize);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
  }

  private get currentConfig(): MeasurementMarketChallengeConfig {
    return MEASUREMENT_MARKET_CHALLENGES[this.challengeIndex];
  }

  private canAct() {
    return !this.leaving && !this.suspended && !this.restored
      && this.challenge.snapshot.phase !== 'complete';
  }

  private readonly handleResize = () => {
    if (this.resizeTimer !== undefined) window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => {
      if (!this.scene.isActive()) return;
      configureNumberKingdomDistrictRenderer(this);
      this.layout();
    }, 80);
  };

  private readonly returnToKeep = () => {
    if (this.leaving) return;
    this.leaving = true;
    saveNumberKingdomDistrictProgress(NUMBER_KINGDOM_DISTRICTS.measureworks, this.progressSnapshot());
    this.scene.start('SubtractionKeep');
  };

  private readonly returnHome = () => {
    if (this.leaving) return;
    this.leaving = true;
    saveNumberKingdomDistrictProgress(NUMBER_KINGDOM_DISTRICTS.measureworks, this.progressSnapshot());
    SaveManager.setLastScene(MEASUREMENT_MARKET_SCENE_KEY);
    SaveManager.save();
    this.scene.start('HomeHub');
  };

  private readonly travelToClocktower = () => {
    if (!this.restored || this.leaving) return;
    if (!canEnterClocktowerPlaza(this.textures)) {
      this.feedbackText.setText('The road is open. Clocktower Plaza is awaiting its runtime QA gate.');
      return audio.click();
    }
    this.leaving = true;
    saveNumberKingdomDistrictProgress(NUMBER_KINGDOM_DISTRICTS.measureworks, this.progressSnapshot());
    this.scene.start('ClocktowerPlaza');
  };

  private shutdown() {
    if (this.challenge) {
      saveNumberKingdomDistrictProgress(NUMBER_KINGDOM_DISTRICTS.measureworks, this.progressSnapshot());
    }
    this.unsubscribeLifecycle?.();
    this.unsubscribeLifecycle = undefined;
    audio.leaveSoundscape({ id: 'number-kingdom' });
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeTimer !== undefined) window.clearTimeout(this.resizeTimer);
    this.input.keyboard?.off('keydown', this.handleKeyDown, this);
    this.goods.forEach(item => item.destroy());
    this.recordTexts.forEach(item => item.destroy());
    this.tweens.killAll();
  }
}
